import { datePiecesFromDateComparator } from './utils/dateComparators';
import { STATES_MAPPING } from './states';
export class ConditionHumanizer {
    condition;
    qualificationMapping;
    qualificationAttributeMapping;
    constructor(condition) {
        if (!condition.payload || !condition.qualifications) {
            throw new Error('Condition must include payload and related qualifications to instantiate ConditionHumanizer');
        }
        this.condition = condition;
        const qualificationMapping = {};
        condition.qualifications.forEach((qualification) => {
            qualificationMapping[qualification.slug] = qualification;
        });
        this.qualificationMapping = qualificationMapping;
        const qualificationAttributeMapping = {};
        const qualificationAttributes = condition.qualifications.flatMap(({ qualification_attributes }) => qualification_attributes);
        qualificationAttributes.forEach((qualificationAttribute) => {
            qualificationAttributeMapping[qualificationAttribute.slug] = qualificationAttribute;
        });
        this.qualificationAttributeMapping = qualificationAttributeMapping;
    }
    /**
     * Geter for a humanized string explanation of the condition payload
     * @returns readable string of all the OR/AND pieces of the condition payload
     */
    get payloadExplanation() {
        const orConditionExplanations = [];
        this.condition.payload?.forEach((orCondition) => {
            orConditionExplanations.push(this.buildConditionExplanation(orCondition));
        });
        return `In order to satisfy this requirement, you must have ${orConditionExplanations.join(' OR ')}`;
    }
    /**
     * Builds the explanation of the condition payload
     */
    buildConditionExplanation(orCondition) {
        return orCondition
            .map((andCondition) => this.buildAndConditionExplanation(andCondition))
            .join(' and ');
    }
    buildAndConditionExplanation(andCondition) {
        const qualificationSlug = Object.keys(andCondition)[0];
        const qualification = this.qualificationMapping[qualificationSlug];
        const qualificationName = qualification?.long_name;
        const attributeCondition = andCondition[qualificationSlug];
        const hasAttributes = Object.values(attributeCondition).length;
        const hasUserInputSource = Object.keys(attributeCondition).some((slug) => this.qualificationAttributeMapping[slug]?.input_source === 'user');
        return hasAttributes && qualification?.qualification_type !== 'q_and_a' && hasUserInputSource
            ? `${qualificationName} with the following attributes: ${this.buildAttributesExplanation(attributeCondition)}`
            : `a valid ${qualificationName}`;
    }
    buildAttributesExplanation(attributeCondition) {
        return Object.entries(attributeCondition)
            .filter(([slug]) => this.qualificationAttributeMapping[slug]?.input_source === 'user')
            .map(([slug, value]) => `${this.qualificationAttributeMapping[slug]?.name} - ${this.translateValue(value, this.qualificationAttributeMapping[slug]?.field_type)}`)
            .join(', ');
    }
    translateValue(value, fieldType) {
        if (typeof value === 'boolean') {
            return value === true ? 'yes' : 'no';
        }
        else if (typeof value === 'object') {
            const key = Object.keys(value)[0];
            const comparatorValue = this.translateDateComparator(value[key]);
            return key === 'gte' ? `within ${comparatorValue}` : `longer than ${comparatorValue} ago`;
        }
        else if (fieldType === 'state') {
            return STATES_MAPPING[value];
        }
        return value;
    }
    translateDateComparator(comparator) {
        const { years, months, days } = datePiecesFromDateComparator(comparator);
        let result = '';
        if (!!years) {
            result += `${years} year `;
        }
        if (!!months) {
            result += `${months} month `;
        }
        if (!!days) {
            result += `${days} day `;
        }
        return result.trim();
    }
}
